import { useEffect, useRef, useState } from "react";
import { Alert, Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import BrandHeaderAtom from "../../../components/atoms/brand-header/brand-header";
import { useAcceptProjectInviteMutation } from "../../../store/services/projectMembers";
import { translateErrorMessage } from "../../../utils/error-handler";
import "./accept-invite.scss";

const resolveAcceptInviteError = (err, t) => {
	const rawKey = err?.originalMessage || err?.data?.message;
	if (!rawKey) {
		return t("accept-invite.error-generic");
	}
	const translated = translateErrorMessage(rawKey);
	return translated !== rawKey
		? translated
		: t("accept-invite.error-generic");
};

function AcceptInvitePage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const token = searchParams.get("token");
	const { isAuthenticated } = useSelector((state) => state.auth) || {};

	const [acceptInvite] = useAcceptProjectInviteMutation();
	const [status, setStatus] = useState("loading");
	const [projectId, setProjectId] = useState(null);
	const [errorMessage, setErrorMessage] = useState("");
	const hasRunRef = useRef(false);

	useEffect(() => {
		if (hasRunRef.current) return;
		hasRunRef.current = true;

		if (!token) {
			setStatus("error");
			setErrorMessage(t("accept-invite.error-token-missing"));
			return;
		}

		(async () => {
			try {
				const result = await acceptInvite({ token }).unwrap();
				setProjectId(result?.projectId || null);
				setStatus("success");
			} catch (err) {
				setStatus("error");
				setErrorMessage(resolveAcceptInviteError(err, t));
			}
		})();
	}, [token, acceptInvite, t]);

	if (status === "success" && projectId) {
		if (isAuthenticated) {
			return <Navigate to={`/projects/${projectId}`} replace />;
		}
		return (
			<div className="accept-invite-page">
				<BrandHeaderAtom />
				<div className="accept-invite-page__content">
					<Alert variant="success">
						{t("accept-invite.success-login-required")}
					</Alert>
					<Button
						variant="primary"
						onClick={() =>
							navigate("/login", {
								state: { redirectTo: `/projects/${projectId}` },
							})
						}
					>
						{t("accept-invite.go-to-login")}
					</Button>
				</div>
			</div>
		);
	}

	return (
		<div className="accept-invite-page">
			<BrandHeaderAtom />
			<div className="accept-invite-page__content">
				{status === "loading" && (
					<p className="accept-invite-page__loading">
						{t("accept-invite.processing")}
					</p>
				)}
				{status === "error" && (
					<>
						<Alert variant="danger">{errorMessage}</Alert>
						<Button
							variant="link"
							onClick={() => navigate("/login")}
						>
							{t("accept-invite.back-to-login")}
						</Button>
					</>
				)}
			</div>
		</div>
	);
}

export default AcceptInvitePage;
